import { styled } from '@mui/material';
import {
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Typography,
  Badge,
} from '@mui/material';

import {
  NAVIGATION_DIMENSIONS,
  NAVIGATION_COLORS,
  NAVIGATION_SPACING,
  NAVIGATION_BORDER_RADIUS,
  NAVIGATION_ANIMATIONS,
  NAVIGATION_Z_INDEX,
} from '../constants/navigationTheme';

const { ASIDE_NAV_WIDTH, BOTTOM_NAV_HEIGHT } = NAVIGATION_DIMENSIONS;

/**
 * Styled Components para a navegação
 */

export const StyledBottomNavigation = styled(BottomNavigation)(({ theme, isDrawerOpen }) => ({
  backgroundColor: NAVIGATION_COLORS.PRIMARY,
  boxShadow: '2px 2px 2px 1px rgba(0, 0, 0, 0.2)',
  height: BOTTOM_NAV_HEIGHT,
  width: '100%',
  position: 'fixed',
  bottom: 0,
  zIndex: NAVIGATION_Z_INDEX.BOTTOM_NAV,
  borderTopLeftRadius: isDrawerOpen ? 0 : NAVIGATION_BORDER_RADIUS.SMALL,
  borderTopRightRadius: isDrawerOpen ? 0 : NAVIGATION_BORDER_RADIUS.SMALL,
  padding: '8px 4px 4px',
  transition: `border-radius ${NAVIGATION_ANIMATIONS.TRANSITION_DURATION} ease`,
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

export const StyledBottomNavigationAction = styled(BottomNavigationAction)(({ theme }) => ({
  color: NAVIGATION_COLORS.TEXT_PRIMARY,
  minWidth: 'auto',
  padding: '8px 4px 4px',
  [theme.breakpoints.down('lg')]: {
    '&.Mui-selected': {
      backgroundColor: 'transparent !important',
      color: NAVIGATION_COLORS.TEXT_PRIMARY,
      fontWeight: 'normal',
    },
  },
  [theme.breakpoints.up('lg')]: {
    '&.Mui-selected': {
      backgroundColor: `${NAVIGATION_COLORS.PRIMARY_DARK} !important`,
      color: NAVIGATION_COLORS.TEXT_PRIMARY,
      fontWeight: 'bold',
      borderRadius: NAVIGATION_BORDER_RADIUS.SMALL,
    },
  },
  '& .MuiBottomNavigationAction-label': {
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    marginTop: 4,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6rem',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  '& svg': {
    width: '1.75rem',
    height: '1.75rem',
    [theme.breakpoints.down('sm')]: {
      width: '1.5rem',
      height: '1.5rem',
    },
  },
}));

export const StyledDrawer = styled(Drawer)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('lg')]: {
    display: 'block',
    '& .MuiDrawer-paper': {
      width: ASIDE_NAV_WIDTH,
      backgroundColor: NAVIGATION_COLORS.PRIMARY,
      marginTop: 0,
      height: '100vh',
      border: 'none',
    },
  },
}));

export const LogoContainer = styled(Box)({
  textAlign: 'center',
  padding: NAVIGATION_SPACING.LOGO_PADDING,
  paddingBottom: NAVIGATION_SPACING.LOGO_PADDING_BOTTOM,
  backgroundColor: NAVIGATION_COLORS.PRIMARY,
  borderBottom: 'none',
  position: 'relative',
  userSelect: 'none',
  '& figure': {
    margin: 0,
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& img': {
    maxHeight: '75px',
    width: 'auto',
    maxWidth: '100%',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '20px',
    right: '20px',
    height: '1px',
    backgroundColor: NAVIGATION_COLORS.DIVIDER,
  },
});

export const StyledListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  padding: NAVIGATION_SPACING.LIST_ITEM_PADDING,
  color: NAVIGATION_COLORS.TEXT_PRIMARY,
  height: NAVIGATION_SPACING.LIST_ITEM_HEIGHT,
  '&:hover': {
    backgroundColor: NAVIGATION_COLORS.PRIMARY_HOVER,
  },
  ...(selected && {
    backgroundColor: `${NAVIGATION_COLORS.PRIMARY_DARK} !important`,
    fontWeight: 'bold',
  }),
}));

export const StyledListItemIcon = styled(ListItemIcon)({
  color: NAVIGATION_COLORS.TEXT_PRIMARY,
  minWidth: 'auto',
  marginRight: NAVIGATION_SPACING.ICON_MARGIN_RIGHT,
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
  },
});

export const StyledListItemText = styled(ListItemText)({
  margin: 0,
  '& .MuiTypography-root': {
    fontSize: '0.875rem',
    color: NAVIGATION_COLORS.TEXT_PRIMARY,
    lineHeight: 1.2,
  },
});

export const StyledBottomDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: NAVIGATION_COLORS.DRAWER_BG,
    borderTopLeftRadius: NAVIGATION_BORDER_RADIUS.LARGE,
    borderTopRightRadius: NAVIGATION_BORDER_RADIUS.LARGE,
    padding: NAVIGATION_SPACING.DRAWER_PADDING,
    maxHeight: '50vh',
    bottom: BOTTOM_NAV_HEIGHT,
    position: 'absolute',
  },
  '& .MuiBackdrop-root': {
  },
}));

export const MoreMenuHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: NAVIGATION_SPACING.MORE_MENU_MARGIN_BOTTOM,
});

export const StyledCloseButton = styled(IconButton)({
  backgroundColor: NAVIGATION_COLORS.CLOSE_BUTTON_BG,
  color: NAVIGATION_COLORS.CLOSE_BUTTON_TEXT,
  '&:hover': {
    backgroundColor: NAVIGATION_COLORS.CLOSE_BUTTON_HOVER,
  },
});

export const MenuItemCard = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: NAVIGATION_SPACING.CARD_PADDING,
  backgroundColor: NAVIGATION_COLORS.DRAWER_BG,
  color: NAVIGATION_COLORS.TEXT_SECONDARY,
  border: `1px solid ${NAVIGATION_COLORS.BORDER}`,
  borderRadius: NAVIGATION_BORDER_RADIUS.SMALL,
  cursor: 'pointer',
  transition: `all ${NAVIGATION_ANIMATIONS.TRANSITION_DURATION} ease`,
  minHeight: NAVIGATION_SPACING.CARD_MIN_HEIGHT,
  [theme.breakpoints.down('lg')]: {
    '&:hover': {
      backgroundColor: NAVIGATION_COLORS.DRAWER_BG,
      transform: 'none',
      boxShadow: 'none',
    },
  },
  [theme.breakpoints.up('lg')]: {
    '&:hover': {
      backgroundColor: selected ? NAVIGATION_COLORS.PRIMARY_DARK : '#e0e0e0',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
  },
  '& svg': {
    fontSize: '2.5rem',
    marginBottom: 8,
    color: NAVIGATION_COLORS.PRIMARY,
    fill: NAVIGATION_COLORS.PRIMARY,
    '& path': {
      fill: '#FFF',
      stroke: NAVIGATION_COLORS.PRIMARY,
    },
  },
}));

export const MenuItemLabel = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 500,
  textAlign: 'center',
  color: NAVIGATION_COLORS.ACCENT,
});

export const PulseBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: 'red',
    color: 'red',
    boxShadow: '0 0 5px red',
    '&::after': {
      position: 'absolute',
      top: -1,
      left: -1,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: `ripple ${NAVIGATION_ANIMATIONS.RIPPLE_DURATION} infinite ease-in-out`,
      border: '2px solid red',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));